import { useTranslate } from "@opensea/next-translate"
import { BigNumber } from "bignumber.js"
import React from "react"
import { graphql, useFragment } from "react-relay"
import { StackedAssetMedia } from "@/components/common/media/StackedAssetMedia"
import type { Purchase_metadata$key } from "@/lib/graphql/__generated__/Purchase_metadata.graphql"
import { bn, display } from "@/lib/helpers/numberUtils"
import { TxItem } from "../components/TxItem.react"
import { useTransactionUrl } from "../hooks/useTransactionUrl"
import type { TxState } from "../types"

type PurchaseProps = {
  metadata: Purchase_metadata$key
  state: TxState
  transactionHash: string | undefined
  nonce?: number
  createdDate?: string
}

export function Purchase({
  metadata: metadataKey,
  state,
  transactionHash,
  nonce,
  createdDate,
}: PurchaseProps) {
  const t = useTranslate("transactions")
  const transactionUrl = useTransactionUrl(transactionHash)

  const { sentAssetQuantities, receivedAssetQuantities } =
    useFragment<Purchase_metadata$key>(
      graphql`
        fragment Purchase_metadata on TransactionMetadataType {
          # What the user paid for the purchase
          sentAssetQuantities {
            asset {
              decimals
              symbol
              usdSpotPrice
            }
            quantity
          }
          receivedAssetQuantities {
            asset {
              displayName
              ...StackedAssetMedia_assets
            }
            quantity
          }
        }
      `,
      metadataKey,
    )

  // Only one payment asset is involved in a purchase transaction
  const paymentAsset = sentAssetQuantities.length
    ? sentAssetQuantities[0].asset
    : undefined
  const paymentSymbol = paymentAsset?.symbol ?? "ETH"
  const paymentUsdSpotPrice = paymentAsset?.usdSpotPrice ?? 1
  const paymentDecimals = paymentAsset?.decimals
  const paymentQuantity = bn(
    BigNumber.sum(...sentAssetQuantities.map(aq => aq.quantity)),
    paymentDecimals,
  )
  const itemQuantity = receivedAssetQuantities.length
    ? bn(BigNumber.sum(...receivedAssetQuantities.map(aq => aq.quantity)))
    : bn(0)

  const description = itemQuantity.isEqualTo(1)
    ? receivedAssetQuantities[0].asset.displayName
    : t("txItem.purchase.description", "{{itemCount}} items", {
        itemCount: display(itemQuantity),
      })

  const isComplete = transactionHash && state !== "PENDING"
  const isPending = state === "PENDING"

  const renderEndEnhancer = () => {
    if (isComplete) {
      return (
        <TxItem.Value
          symbol={paymentSymbol}
          unit={paymentQuantity}
          usd={paymentQuantity.times(paymentUsdSpotPrice)}
        />
      )
    }
    if (isPending) {
      return (
        <TxItem.Pending
          createdDate={createdDate}
          href={transactionUrl}
          nonce={nonce}
        />
      )
    }
    return <TxItem.Requested />
  }

  return (
    <TxItem href={isPending ? undefined : transactionUrl} target="_blank">
      <TxItem.Avatar state={state}>
        <StackedAssetMedia
          assets={receivedAssetQuantities.map(aq => aq.asset)}
        />
      </TxItem.Avatar>
      <TxItem.Content>
        <TxItem.Title>{t("txItem.purchase.title", "Purchase")}</TxItem.Title>
        <TxItem.Description>{description}</TxItem.Description>
      </TxItem.Content>
      {renderEndEnhancer()}
    </TxItem>
  )
}
